import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';

const ADMIN_EMAIL = 'boookssales@gmail.com';

const fmt = (n) => `$${(n || 0).toFixed(2)}`;
const fmtK = (n) => n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : fmt(n);

const PLAN_COLORS = {
    onetime: '#e94560',
    monthly: '#0f3460',
    yearly:  '#27ae60',
    other:   '#95a5a6',
};
const PLAN_LABELS = {
    onetime: 'One-Time',
    monthly: 'Monthly',
    yearly:  'Yearly',
    other:   'Other',
};

// ── Lightweight SVG bar chart ─────────────────────────────────────────────────
function BarChart({ data, height = 180 }) {
    if (!data || data.length === 0) return null;
    const maxVal = Math.max(...data.map(d => d.revenue), 1);
    const w = 100 / data.length;
    return (
        <svg viewBox={`0 0 100 ${height}`} className="w-full" style={{ height }}>
            {data.map((d, i) => {
                const barH = Math.max(2, (d.revenue / maxVal) * (height - 28));
                const x = i * w + w * 0.15;
                const bw = w * 0.7;
                const y = height - 20 - barH;
                return (
                    <g key={d.key}>
                        <rect x={x} y={y} width={bw} height={barH} rx="1.5"
                            fill={d.revenue > 0 ? '#e94560' : '#e5e7eb'} opacity="0.9" />
                        {d.revenue > 0 && (
                            <text x={x + bw / 2} y={y - 2} textAnchor="middle"
                                fontSize="3.5" fill="#374151" fontWeight="600">
                                {fmtK(d.revenue)}
                            </text>
                        )}
                        <text x={x + bw / 2} y={height - 6} textAnchor="middle"
                            fontSize="3" fill="#9ca3af">
                            {d.label}
                        </text>
                    </g>
                );
            })}
        </svg>
    );
}

// ── Lightweight SVG donut pie chart ───────────────────────────────────────────
function DonutChart({ segments, size = 140 }) {
    const cx = size / 2, cy = size / 2, r = size * 0.38, inner = r * 0.55;
    const total = segments.reduce((s, d) => s + d.value, 0) || 1;
    let angle = -Math.PI / 2;
    const paths = segments.map(seg => {
        const sweep = (seg.value / total) * 2 * Math.PI;
        const x1 = cx + r * Math.cos(angle);
        const y1 = cy + r * Math.sin(angle);
        angle += sweep;
        const x2 = cx + r * Math.cos(angle);
        const y2 = cy + r * Math.sin(angle);
        const li = sweep > Math.PI ? 1 : 0;
        const xi1 = cx + inner * Math.cos(angle);
        const yi1 = cy + inner * Math.sin(angle);
        const xi2 = cx + inner * Math.cos(angle - sweep);
        const yi2 = cy + inner * Math.sin(angle - sweep);
        return { ...seg, d: `M${x1},${y1} A${r},${r} 0 ${li},1 ${x2},${y2} L${xi1},${yi1} A${inner},${inner} 0 ${li},0 ${xi2},${yi2} Z` };
    });
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            {paths.map((p, i) => (
                <path key={i} d={p.d} fill={p.color} opacity="0.9" />
            ))}
            <circle cx={cx} cy={cy} r={inner} fill="white" />
            <text x={cx} y={cy - 4} textAnchor="middle" fontSize="10" fontWeight="700" fill="#1a1a2e">
                {total}
            </text>
            <text x={cx} y={cy + 8} textAnchor="middle" fontSize="5" fill="#9ca3af">payments</text>
        </svg>
    );
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, color = '#e94560', bg }) {
    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                style={{ background: bg || `${color}18` }}>
                <i className={`fas ${icon} text-sm`} style={{ color }}></i>
            </div>
            <div className="text-xs text-gray-500 font-medium mb-0.5">{label}</div>
            <div className="text-2xl font-extrabold" style={{ color }}>{value}</div>
            {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
        </div>
    );
}

export default function Admin() {
    const { user, authFetch } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('overview');
    const [userFilter, setUserFilter] = useState('all');
    const [paymentSearch, setPaymentSearch] = useState('');

    useEffect(() => {
        if (user && user.email !== ADMIN_EMAIL) {
            navigate('/dashboard');
            return;
        }
        fetch();
    }, [user]);

    const fetch = async () => {
        setLoading(true);
        try {
            const res = await authFetch('/api/admin/stats');
            if (!res.ok) {
                const e = await res.json();
                throw new Error(e.error || 'Access denied');
            }
            setData(await res.json());
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <LoadingSpinner fullPage text="Loading admin dashboard..." />;

    if (error) return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-lock text-2xl text-red-500"></i>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
                <p className="text-gray-500 text-sm">{error}</p>
                <Link to="/dashboard" className="inline-block mt-4 px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: '#e94560' }}>
                    Back to Dashboard
                </Link>
            </div>
        </div>
    );

    if (!data) return null;

    const { summary, planBreakdown, monthlyBreakdown, payments, users, recentReports } = data;

    // Filter payments
    const filteredPayments = payments.filter(p =>
        !paymentSearch || p.email.toLowerCase().includes(paymentSearch.toLowerCase()) ||
        p.name.toLowerCase().includes(paymentSearch.toLowerCase()) ||
        p.plan.toLowerCase().includes(paymentSearch.toLowerCase())
    );

    // Filter users
    const filteredUsers = users.filter(u => {
        if (userFilter === 'active') return u.subscriptionStatus === 'active';
        if (userFilter === 'inactive') return u.subscriptionStatus !== 'active';
        return true;
    });

    // Pie chart segments
    const pieSegments = Object.entries(planBreakdown.counts)
        .filter(([, v]) => v > 0)
        .map(([plan, count]) => ({
            label:  PLAN_LABELS[plan] || plan,
            value:  count,
            color:  PLAN_COLORS[plan] || '#ccc',
            revenue: planBreakdown.totals[plan] || 0,
        }));

    const TABS = [
        { id: 'overview',  label: 'Overview',   icon: 'fa-chart-line' },
        { id: 'payments',  label: 'Payments',   icon: 'fa-credit-card' },
        { id: 'users',     label: 'Users',      icon: 'fa-users' },
        { id: 'reports',   label: 'Reports',    icon: 'fa-file-alt' },
    ];

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 rounded text-xs font-bold text-white" style={{ background: '#e94560' }}>ADMIN</span>
                        <h1 className="text-2xl font-extrabold" style={{ color: '#1a1a2e' }}>Admin Dashboard</h1>
                    </div>
                    <p className="text-gray-500 text-sm">Seller Source · Revenue & User Analytics</p>
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={fetch} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border-2 border-gray-200 hover:bg-gray-50 text-gray-700">
                        <i className="fas fa-sync-alt text-xs"></i> Refresh
                    </button>
                    <Link to="/dashboard" className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white" style={{ background: '#1a1a2e' }}>
                        <i className="fas fa-arrow-left text-xs"></i> Dashboard
                    </Link>
                </div>
            </div>

            {/* Summary Stat Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard icon="fa-dollar-sign"   label="Total Revenue"       value={fmtK(summary.totalRevenue)}  sub={`${summary.totalPayments} payments`} color="#e94560" />
                <StatCard icon="fa-calendar"       label="This Month"          value={fmtK(summary.monthRevenue)}  sub="current month"                        color="#0f3460" />
                <StatCard icon="fa-chart-bar"      label="Year to Date"        value={fmtK(summary.ytdRevenue)}    sub={`Jan–${new Date().toLocaleDateString('en-US',{month:'short'})} ${new Date().getFullYear()}`} color="#27ae60" />
                <StatCard icon="fa-users"          label="Active Subscribers"  value={summary.activeSubscribers}   sub={`of ${summary.totalUsers} users`}      color="#8e44ad" />
            </div>

            {/* Secondary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard icon="fa-file-alt"   label="Total Reports"   value={summary.totalReports}  sub="all time"              color="#2980b9" />
                <StatCard icon="fa-file-alt"   label="Reports / Month" value={summary.monthReports}  sub="this month"            color="#e67e22" />
                <StatCard icon="fa-user-plus"  label="Total Users"     value={summary.totalUsers}    sub="registered accounts"   color="#16a085" />
                <StatCard icon="fa-receipt"    label="Payments Total"  value={summary.totalPayments} sub="all time"              color="#c0392b" />
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-8 overflow-x-auto">
                {TABS.map(t => (
                    <button key={t.id} onClick={() => setActiveTab(t.id)}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all whitespace-nowrap flex-1 justify-center ${activeTab === t.id ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                        <i className={`fas ${t.icon} text-xs`}></i>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* ── OVERVIEW TAB ──────────────────────────────────────────── */}
            {activeTab === 'overview' && (
                <div className="space-y-6">
                    {/* Revenue bar chart */}
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="font-bold text-gray-900">Monthly Revenue</h3>
                                <p className="text-xs text-gray-500 mt-0.5">Last 12 months</p>
                            </div>
                            <div className="text-right">
                                <div className="text-xl font-extrabold" style={{ color: '#e94560' }}>{fmtK(summary.ytdRevenue)}</div>
                                <div className="text-xs text-gray-400">Year to date</div>
                            </div>
                        </div>
                        <BarChart data={monthlyBreakdown} height={180} />
                    </div>

                    {/* Plan breakdown */}
                    <div className="grid lg:grid-cols-2 gap-6">
                        {/* Donut chart */}
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                            <h3 className="font-bold text-gray-900 mb-5">Plan Type Distribution</h3>
                            <div className="flex items-center gap-8">
                                <DonutChart segments={pieSegments} size={140} />
                                <div className="flex-1 space-y-3">
                                    {pieSegments.map(seg => (
                                        <div key={seg.label}>
                                            <div className="flex items-center justify-between text-sm mb-1">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-3 h-3 rounded-full" style={{ background: seg.color }}></div>
                                                    <span className="font-medium text-gray-700">{seg.label}</span>
                                                </div>
                                                <span className="font-bold text-gray-900">{seg.value} sales</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                                                    <div className="h-1.5 rounded-full" style={{
                                                        width: `${(seg.value / (pieSegments.reduce((s,x)=>s+x.value,0)||1)) * 100}%`,
                                                        background: seg.color
                                                    }}></div>
                                                </div>
                                                <span className="text-xs font-semibold text-gray-600 w-14 text-right">{fmtK(seg.revenue)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Revenue by plan bar */}
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                            <h3 className="font-bold text-gray-900 mb-5">Revenue by Plan</h3>
                            <div className="space-y-4">
                                {Object.entries(planBreakdown.totals).map(([plan, revenue]) => {
                                    const maxRev = Math.max(...Object.values(planBreakdown.totals), 1);
                                    const pct = (revenue / maxRev) * 100;
                                    return (
                                        <div key={plan}>
                                            <div className="flex items-center justify-between text-sm mb-1.5">
                                                <span className="font-semibold text-gray-700">{PLAN_LABELS[plan]}</span>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-xs text-gray-400">{planBreakdown.counts[plan]} sales</span>
                                                    <span className="font-bold text-gray-900">{fmt(revenue)}</span>
                                                </div>
                                            </div>
                                            <div className="w-full bg-gray-100 rounded-full h-2.5">
                                                <div className="h-2.5 rounded-full transition-all duration-500"
                                                    style={{ width: `${pct}%`, background: PLAN_COLORS[plan] || '#ccc' }}></div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="mt-5 pt-4 border-t border-gray-100 flex justify-between text-sm">
                                <span className="text-gray-500 font-medium">Total Revenue</span>
                                <span className="font-extrabold text-gray-900">{fmt(summary.totalRevenue)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Recent payments */}
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                        <h3 className="font-bold text-gray-900 mb-4">Recent Payments</h3>
                        <PaymentTable payments={payments.slice(0, 10)} />
                    </div>
                </div>
            )}

            {/* ── PAYMENTS TAB ──────────────────────────────────────────── */}
            {activeTab === 'payments' && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-5 gap-3">
                        <div>
                            <h3 className="font-bold text-gray-900">All Payments</h3>
                            <p className="text-xs text-gray-500 mt-0.5">{payments.length} total · {fmt(summary.totalRevenue)} collected</p>
                        </div>
                        <div className="relative">
                            <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                            <input value={paymentSearch} onChange={e => setPaymentSearch(e.target.value)}
                                placeholder="Search by email or plan..."
                                className="pl-8 pr-4 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-red-400 w-60" />
                        </div>
                    </div>
                    <PaymentTable payments={filteredPayments} />
                </div>
            )}

            {/* ── USERS TAB ─────────────────────────────────────────────── */}
            {activeTab === 'users' && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-5 gap-3">
                        <div>
                            <h3 className="font-bold text-gray-900">All Users</h3>
                            <p className="text-xs text-gray-500 mt-0.5">{users.length} registered · {summary.activeSubscribers} active</p>
                        </div>
                        <div className="flex gap-2">
                            {['all', 'active', 'inactive'].map(f => (
                                <button key={f} onClick={() => setUserFilter(f)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${userFilter === f ? 'text-white shadow' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                    style={userFilter === f ? { background: '#e94560' } : {}}>
                                    {f}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b-2 border-gray-100">
                                    <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase">User</th>
                                    <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase">Plan</th>
                                    <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase">Status</th>
                                    <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase">Reports Today</th>
                                    <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase">Joined</th>
                                    <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase">Sub Ends</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((u, i) => (
                                    <tr key={u.id} className={`border-b border-gray-50 hover:bg-gray-50 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                                        <td className="py-3 px-2">
                                            <div className="font-medium text-gray-900">{u.name || '—'}</div>
                                            <div className="text-xs text-gray-400">{u.email}</div>
                                        </td>
                                        <td className="py-3 px-2">
                                            <span className="px-2 py-0.5 rounded text-xs font-bold capitalize"
                                                style={{
                                                    background: `${PLAN_COLORS[u.plan] || '#95a5a6'}18`,
                                                    color:      PLAN_COLORS[u.plan] || '#666'
                                                }}>
                                                {u.plan || 'none'}
                                            </span>
                                        </td>
                                        <td className="py-3 px-2">
                                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${u.subscriptionStatus === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                {u.subscriptionStatus === 'active' ? '● Active' : '○ Inactive'}
                                            </span>
                                        </td>
                                        <td className="py-3 px-2 font-semibold text-gray-700">{u.reportsToday}</td>
                                        <td className="py-3 px-2 text-xs text-gray-500">{new Date(u.joinedAt).toLocaleDateString()}</td>
                                        <td className="py-3 px-2 text-xs text-gray-500">
                                            {u.subscriptionEnd ? new Date(u.subscriptionEnd).toLocaleDateString() : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredUsers.length === 0 && (
                            <div className="text-center py-8 text-gray-400 text-sm">No users found</div>
                        )}
                    </div>
                </div>
            )}

            {/* ── REPORTS TAB ────────────────────────────────────────────── */}
            {activeTab === 'reports' && (
                <div className="space-y-6">
                    {/* Top categories */}
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                        <h3 className="font-bold text-gray-900 mb-5">Recent Report Activity</h3>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b-2 border-gray-100">
                                        <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase">User</th>
                                        <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase">Category</th>
                                        <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase">Product Type</th>
                                        <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase">Generated</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentReports.map((r, i) => (
                                        <tr key={r.id} className={`border-b border-gray-50 hover:bg-gray-50 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                                            <td className="py-3 px-2 text-xs text-gray-600">{r.email}</td>
                                            <td className="py-3 px-2">
                                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                                                    {r.category || '—'}
                                                </span>
                                            </td>
                                            <td className="py-3 px-2 text-xs text-gray-600">{r.productType || '—'}</td>
                                            <td className="py-3 px-2 text-xs text-gray-500">
                                                {new Date(r.createdAt).toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {recentReports.length === 0 && (
                                <div className="text-center py-8 text-gray-400 text-sm">No reports yet</div>
                            )}
                        </div>
                    </div>

                    {/* Report stats summary */}
                    <div className="grid sm:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                            <h3 className="font-bold text-gray-900 mb-4">Reports Summary</h3>
                            <div className="space-y-3">
                                <div className="flex justify-between text-sm"><span className="text-gray-500">Total Reports Generated</span><span className="font-bold">{summary.totalReports}</span></div>
                                <div className="flex justify-between text-sm"><span className="text-gray-500">This Month</span><span className="font-bold">{summary.monthReports}</span></div>
                                <div className="flex justify-between text-sm"><span className="text-gray-500">Daily Avg (30-day)</span><span className="font-bold">{(summary.monthReports / 30).toFixed(1)}</span></div>
                                <div className="flex justify-between text-sm"><span className="text-gray-500">Per Active User</span>
                                    <span className="font-bold">
                                        {summary.activeSubscribers > 0 ? (summary.totalReports / summary.activeSubscribers).toFixed(1) : '—'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                            <h3 className="font-bold text-gray-900 mb-4">Revenue per Report</h3>
                            <div className="space-y-3">
                                <div className="flex justify-between text-sm"><span className="text-gray-500">Avg Revenue / Report</span>
                                    <span className="font-bold">{summary.totalReports > 0 ? fmt(summary.totalRevenue / summary.totalReports) : '—'}</span>
                                </div>
                                <div className="flex justify-between text-sm"><span className="text-gray-500">One-Time Revenue</span><span className="font-bold">{fmt(planBreakdown.totals.onetime)}</span></div>
                                <div className="flex justify-between text-sm"><span className="text-gray-500">Monthly Sub Revenue</span><span className="font-bold">{fmt(planBreakdown.totals.monthly)}</span></div>
                                <div className="flex justify-between text-sm"><span className="text-gray-500">Yearly Sub Revenue</span><span className="font-bold">{fmt(planBreakdown.totals.yearly)}</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Payment table component ───────────────────────────────────────────────────
function PaymentTable({ payments }) {
    if (!payments || payments.length === 0) {
        return <div className="text-center py-8 text-gray-400 text-sm">No payments found</div>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b-2 border-gray-100">
                        <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase">Customer</th>
                        <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase">Plan</th>
                        <th className="text-right py-3 px-2 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                        <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase">Status</th>
                        <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase">Date & Time</th>
                        <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase">Stripe ID</th>
                    </tr>
                </thead>
                <tbody>
                    {payments.map((p, i) => {
                        const planColor = PLAN_COLORS[p.plan?.toLowerCase()] || '#95a5a6';
                        return (
                            <tr key={p.id} className={`border-b border-gray-50 hover:bg-gray-50 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                                <td className="py-3 px-2">
                                    <div className="font-medium text-gray-900">{p.name || '—'}</div>
                                    <div className="text-xs text-gray-400">{p.email}</div>
                                </td>
                                <td className="py-3 px-2">
                                    <span className="px-2 py-0.5 rounded text-xs font-bold capitalize"
                                        style={{ background: `${planColor}18`, color: planColor }}>
                                        {PLAN_LABELS[p.plan?.toLowerCase()] || p.plan || '—'}
                                    </span>
                                </td>
                                <td className="py-3 px-2 text-right font-bold" style={{ color: '#27ae60' }}>
                                    {fmt(p.amount)}
                                </td>
                                <td className="py-3 px-2">
                                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${p.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                        {p.status || 'paid'}
                                    </span>
                                </td>
                                <td className="py-3 px-2 text-xs text-gray-500 whitespace-nowrap">
                                    <div>{new Date(p.createdAt).toLocaleDateString()}</div>
                                    <div className="text-gray-400">{new Date(p.createdAt).toLocaleTimeString()}</div>
                                </td>
                                <td className="py-3 px-2 text-xs text-gray-400 font-mono">
                                    {p.stripeId ? p.stripeId.slice(0, 20) + '…' : '—'}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
